/**
 * The native-language review surface — spec 08 §2, drawn in the page flow beside the note it is
 * about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN IS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A UM has written an observation in her own language. This is where she reads the English that
 * was made from it, beside her own words, and corrects whichever of the two needs correcting
 * before anybody else sees anything. Spec 08's caption: *"the Korean original is translated and
 * kept private; only the English draft — corrected by its author first — is ever released."*
 *
 * **IT IS ONLY EVER SHOWN TO THE NOTE'S AUTHOR** (§5, last row: *"This screen is only ever shown
 * to the note's author. There is no read-only variant"*). It is rendered from `GET /notes/mine`,
 * which is the only surface in the API that returns `bodyNative` at all, so there is no shape of
 * this component that could be handed somebody else's note.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE REVIEW GATE, AND WHERE EACH HALF OF IT LIVES
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2.5: *"Nothing translated reaches anyone until the author has read both versions side by
 * side. That gate is real and must not be bypassable — there is no 'release without reading'
 * path."*
 *
 * A server cannot watch somebody read. What it can do is refuse, and what a screen can do is
 * make the act that lifts the refusal reachable from ONE place. So the gate is two halves:
 *
 *  * **The server's half, which cannot be skipped.** `POST /notes/release` refuses any native
 *    note whose `ai_state` is not `none` — an unread draft, a stale one, or a failure — and
 *    nothing in the release path can clear that state on the author's behalf. A stale build, a
 *    second tab or a script talking to the API directly is refused exactly as this screen is.
 *  * **This component's half, which is what makes the act mean "I have read it".** The two
 *    things that clear the state are editing the English and pressing `Release English Version`
 *    here — and `Release English Version` exists on THIS component and nowhere else in the
 *    application. It sits under the English draft, which sits under the original, so the act is
 *    taken on the screen that has both versions on it.
 *
 * **`Release English Version` therefore sends TWO calls in order: confirm, then release.** Not
 * one call that does both — a release endpoint that confirmed for you would be the *"release
 * without reading"* path, whoever called it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS HONESTLY ABSENT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **The bilingual tag chips of §2.7 render their ENGLISH label only.** §2.7 draws
 *     `위치선정` above `Positioning`, and gap A1's answer is *"one language-neutral tag id
 *     underneath, two labels on top"*. The id is language-neutral and always has been
 *     (`CAPTURE_TAP_TAGS`), but the second label does not exist: there is no per-language tag
 *     label anywhere in this codebase's data, seeded or otherwise. Writing seven Korean strings
 *     here would be inventing the panel's own vocabulary in a language none of us checked, so
 *     the chips carry the English label and the footer says why in the author's own sight.
 *  2. **`Check This Translation Carefully` renders only when the model actually flagged the
 *     answer.** §5's `Low confidence` row is conditional on the model flagging uncertainty, and
 *     the flag is a real contract — `TRANSLATION_UNCERTAIN_MARKER` in the prompt, stripped and
 *     reported by the server. A screen that showed the warning on every draft would be a screen
 *     whose warning means nothing. It is a session fact, not a stored one: the receipt reports
 *     it, and a reload of the list does not bring it back, because what is stored is the text.
 *  3. **The chrome is English.** Q-08b rules that onboarding and the capture flow are translated
 *     first and in their own slice; nothing here pretends otherwise.
 */

import { useState } from "react";

import {
  CAPTURE_TAP_TAGS,
  languageByCode,
  languagePill,
  originalPanelTitle,
  privacyRowTitle,
  retranslateHint,
} from "@hockeytd/shared";

import { confirmTranslation, editNativeText, editNoteText, translateNote } from "../../api/client.js";
import type { OwnNoteView } from "../../api/types.js";

export interface NativeNoteReviewProps {
  /** The note under review. Always the caller's own, and always `draft` or `locked`. */
  note: OwnNoteView;
  /** Re-read `GET /notes/mine`: the list must show what the SERVER holds, never what we sent. */
  onChanged: () => void;
  /**
   * The parent's single release path. It is passed in rather than called here so that this
   * application keeps ONE release call, in `CapturePanel`, with one receipt and one failure
   * line — the same reason the API keeps one release route.
   */
  onRelease: (noteId: string) => void;
  /** True while the parent's release call is in flight, so the button can say so. */
  releasing: boolean;
  /** Close the review surface. */
  onClose: () => void;
}

/** §2.6's second line. Fixed — nothing in it names a language, so nothing in it is templated. */
export const PRIVACY_ROW_SUBLINE = "Only The English Version Is Released";

/**
 * §2.5's warn-toned tag, verbatim. Exported so a test asserts the shipped string rather than a
 * paraphrase of it, and so there is exactly one copy of it in the build.
 */
export const AI_DRAFT_TAG = "AI Draft — Review Before Release";

/** §5's `Low confidence` rendering, verbatim, and shown only when the model flagged its answer. */
export const LOW_CONFIDENCE_NOTE = "Check This Translation Carefully";

/** §5's `Offline` rendering, verbatim. */
export const OFFLINE_ENGLISH_LINE = "Will Translate When You’re Back Online";

/** §5's failure heading, verbatim. Its three routes are the three buttons under it. */
export const TRANSLATION_FAILED_LINE = "Translation Did Not Work";

/** §2.5's right-hand sub-line, verbatim. */
export const NOT_YET_RELEASED = "Not Yet Released";

/** §2.9's footer, in its own words. */
export const TAGS_ARE_NEUTRAL_FOOT = "Tags Are Language-Neutral Underneath";

/**
 * What the English panel is showing, derived rather than stored.
 *
 * Four of the five come straight from `note.aiState` (see `note.ai_state`'s column comment,
 * migration 049). The fifth — `translating` — is this component's own in-flight state and
 * exists nowhere else: a stored `translating` would be a state a crashed request could strand a
 * note in for ever, which is the one thing spec 12 §1.2 forbids.
 *
 * `untranslated` is a native note with no English and nothing pending. It is what a capture made
 * with no signal looks like, and §5's `Offline` row is the sentence for it.
 */
type EnglishPanelState =
  | "translating"
  | "translated"
  | "stale"
  | "failed"
  | "untranslated"
  | "reviewed";

function panelState(note: OwnNoteView, inFlight: boolean): EnglishPanelState {
  if (inFlight) return "translating";
  if (note.aiState === "translated") return "translated";
  if (note.aiState === "stale") return "stale";
  if (note.aiState === "failed") return "failed";
  // `none` with no English is a note nothing has been asked of yet; `none` with English is a
  // draft its author has edited, typed or confirmed — the only state the release gate passes.
  return note.bodyEn === "" ? "untranslated" : "reviewed";
}

/** The label a tag id renders as. The palette and the API's validator share one constant. */
function tagLabel(tagId: string): string {
  return CAPTURE_TAP_TAGS.find((tag) => tag.tag_id === tagId)?.label ?? tagId;
}

export function NativeNoteReview({
  note,
  onChanged,
  onRelease,
  releasing,
  onClose,
}: NativeNoteReviewProps): React.JSX.Element | null {
  /**
   * The author's typing in the ORIGINAL panel. Seeded from the stored text and **never reset by
   * a failed save** — a save that did not land must leave the words on screen, because after a
   * failure they exist nowhere else. Same rule as `CapturePanel`'s English editor.
   */
  const [original, setOriginal] = useState(note.bodyNative ?? "");
  /** The author's typing in the ENGLISH panel. Seeded from the stored draft, same rule. */
  const [english, setEnglish] = useState(note.bodyEn);

  const [busy, setBusy] = useState<null | "original" | "translate" | "english" | "confirm">(null);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * §5's `Low confidence`. A SESSION fact and not a stored one: the model flagged THIS answer,
   * the receipt said so, and a reload does not bring it back because what is stored is the text.
   */
  const [uncertain, setUncertain] = useState(false);

  const language = note.nativeLang === null ? undefined : languageByCode(note.nativeLang);
  // A note whose stored language this build does not know cannot be drawn: there is no endonym
  // for the pill and no font stack for the original. Rendering it in a fallback face and calling
  // it Korean would be worse than not rendering it, so the row simply keeps its ordinary shape.
  if (note.bodyNative === null || language === undefined) return null;

  const state = panelState(note, busy === "translate");
  const originalChanged = original.trim() !== note.bodyNative;
  const englishChanged = english.trim() !== note.bodyEn;

  /** A failure never loses anything, so every catch below says so and keeps the typing. */
  const failed = (error: unknown, fallback: string): void => {
    setFailure(error instanceof Error ? error.message : fallback);
  };

  /**
   * Correct the original, then re-translate — §2.4's *"Fixing the {language} re-translates the
   * English"*, as two calls in order.
   *
   * Two rather than one because the correction must not depend on the translation succeeding
   * (spec 12 §1.2): the original is saved first, and if the call that follows it fails, the
   * correction is still stored and the English panel says what happened.
   */
  const saveOriginal = (): void => {
    if (busy !== null || !originalChanged) return;
    setBusy("original");
    setFailure(null);
    editNativeText(note.id, original.trim()).then(
      (receipt) => {
        setBusy(null);
        onChanged();
        // Stale means there WAS an English draft and it no longer matches. Nothing to
        // re-translate otherwise; the author asks for the first translation themselves.
        if (receipt.aiState === "stale") retranslate();
      },
      (error: unknown) => {
        setBusy(null);
        failed(error, "The correction was not saved.");
      },
    );
  };

  /**
   * Call 2 — §5's `Try Again`, and the first translation too.
   *
   * A refusal from the model is not an exception: the receipt carries the sentence and the
   * state, and the note is exactly as complete as it was. Only a failed REQUEST throws, and it
   * gets the ordinary failure line.
   */
  const retranslate = (): void => {
    setBusy("translate");
    setFailure(null);
    setUncertain(false);
    translateNote(note.id).then(
      (receipt) => {
        setBusy(null);
        setUncertain(receipt.uncertain);
        if (receipt.detail !== null) setFailure(receipt.detail);
        // Re-read rather than patch: what the panel shows is what the server stored.
        onChanged();
      },
      (error: unknown) => {
        setBusy(null);
        failed(error, "The translation did not run. Your note is safe.");
      },
    );
  };

  /**
   * Save the English — §2.5's editable draft, and §5's `Type It Yourself`.
   *
   * **This IS the review** (spec 08 §2.5): an author who has rewritten the draft has read it,
   * and the API clears the AI marker on this call for exactly that reason. It is therefore the
   * second of the two ways through the gate, and the shorter one.
   */
  const saveEnglish = (): void => {
    if (busy !== null || !englishChanged) return;
    setBusy("english");
    setFailure(null);
    editNoteText(note.id, english.trim()).then(
      () => {
        setBusy(null);
        onChanged();
      },
      (error: unknown) => {
        setBusy(null);
        failed(error, "The English was not saved.");
      },
    );
  };

  /**
   * §5's `Release Tags Only` — *"releases the language-neutral tags and the criterion, with no
   * prose, a real, useful observation the panel can act on"*.
   *
   * **It is the English edit with an empty string, and that is the honest representation.** The
   * note released this way is a tag-only note, indistinguishable from one captured by tapping a
   * chip and pressing Save — which is what Q-02b has always said a tag-only note is: a complete
   * observation. There is no `withheld` flag, no placeholder row and no "prose not available"
   * marker anywhere in the released projection, so the panel is never shown an absence dressed
   * up as something being kept from them.
   *
   * It is offered only on a note that carries a tag: blanking a tag-less note's English would
   * leave nothing at all, and the API refuses it with a typed 422.
   */
  const releaseTagsOnly = (): void => {
    if (busy !== null || note.tagIds.length === 0) return;
    setBusy("english");
    setFailure(null);
    editNoteText(note.id, "").then(
      () => {
        setBusy(null);
        setEnglish("");
        onChanged();
        onRelease(note.id);
      },
      (error: unknown) => {
        setBusy(null);
        failed(error, "The note was not changed, and nothing was released.");
      },
    );
  };

  /**
   * `Release English Version` — §2.8's primary, and the review gate's client half.
   *
   * Confirm, THEN release. The confirmation says "I have read this beside my own words" and is
   * sent from this screen and no other; the release then passes the server's gate because the
   * state it refuses has been cleared by a deliberate act of the author's, not by the release
   * itself. If the confirmation fails, nothing is released — which is right: an unreviewed
   * translation must never reach the panel because a second call happened to succeed.
   */
  const releaseEnglish = (): void => {
    if (busy !== null || releasing) return;
    setBusy("confirm");
    setFailure(null);
    confirmTranslation(note.id).then(
      () => {
        setBusy(null);
        onRelease(note.id);
      },
      (error: unknown) => {
        setBusy(null);
        failed(error, "Nothing was released. Try again when you have signal.");
      },
    );
  };

  /**
   * §2.4's behaviour, said plainly rather than implied: release is unavailable until the English
   * is something the author stands behind. It mirrors the server's own gate exactly, so the
   * button is never live over a press the API would refuse.
   */
  const releasable = state === "reviewed" || state === "translated";

  return (
    <div className="natrev" data-testid={`native-review-${note.id}`}>
      <div className="natrev__head">
        <span className="card__kicker">Native-Language Note</span>
        {/* §2.1's language pill: `한국어 → English`, the endonym because it names her own side. */}
        <span className="pill pill--acc">{languagePill(language)}</span>
      </div>

      {/* ── §2.3 · the ORIGINAL panel. Editable: correcting it is the primary path. ── */}
      <div className="natrev__panel">
        <div className="natrev__panelhead">
          <span className="card__kicker">{originalPanelTitle(language)}</span>
          {/*
           * §2.3's language chip — `KO`, upper-case, in the mono face. **Never a duration.**
           * This slot carried a duration-shaped marker until v1.2.2 and it was replaced
           * precisely because a duration implies a recording, and there is no audio anywhere in
           * this system (decision B3).
           */}
          <span className="natrev__chip num">{language.chip}</span>
        </div>
        <textarea
          className="natrev__native"
          // §2.3: "a font stack with appropriate CJK fallbacks … The build needs an equivalent
          // per supported script." It comes from the language table, not from a class per
          // language, so an eighth language needs a row and not a stylesheet.
          style={{ fontFamily: language.fontStack }}
          value={original}
          rows={4}
          aria-label={`Correct the ${language.name} original`}
          onChange={(event) => {
            setOriginal(event.target.value);
          }}
          onBlur={saveOriginal}
        />
        {/* §2.4's hint, templated on the language exactly as §6 requires. */}
        <p className="natrev__hint">
          <span aria-hidden="true">↻ </span>
          {retranslateHint(language)}
        </p>
        {originalChanged ? (
          <button
            type="button"
            className="gbtn gbtn--sm"
            data-testid={`native-save-original-${note.id}`}
            disabled={busy !== null || original.trim() === ""}
            onClick={saveOriginal}
          >
            {busy === "original" ? "Saving…" : "Save The Original"}
          </button>
        ) : null}
      </div>

      {/* ── §2.5 · the ENGLISH draft panel ─────────────────────────────────── */}
      <div className="natrev__panel">
        <div className="natrev__panelhead">
          <span className="card__kicker">English Draft</span>
          {state === "translated" || state === "stale" ? (
            <span className="natrev__aitag">{AI_DRAFT_TAG}</span>
          ) : null}
        </div>

        {state === "translating" ? <p className="natrev__state">Translating…</p> : null}
        {state === "stale" ? <p className="natrev__state">Re-Translating…</p> : null}
        {state === "untranslated" ? (
          <p className="natrev__state">{OFFLINE_ENGLISH_LINE}</p>
        ) : null}
        {/*
         * §5's `Low confidence`, and only when the model actually flagged this answer. See the
         * module header on why it is not shown by default.
         */}
        {uncertain && state === "translated" ? (
          <p className="natrev__uncertain">{LOW_CONFIDENCE_NOTE}</p>
        ) : null}

        {state === "failed" ? (
          <>
            <p className="natrev__failed">{TRANSLATION_FAILED_LINE}</p>
            {/*
             * §5's three routes, and **none of them loses the note**: the original is stored,
             * untouched, and is still on the screen above. This closes gap S7 — a failure must
             * never strand a note behind a private original.
             */}
            <div className="natrev__acts">
              <button
                type="button"
                className="gbtn gbtn--sm"
                data-testid={`native-try-again-${note.id}`}
                disabled={busy !== null}
                onClick={retranslate}
              >
                Try Again
              </button>
              {note.tagIds.length > 0 ? (
                <button
                  type="button"
                  className="gbtn gbtn--sm"
                  data-testid={`native-tags-only-${note.id}`}
                  disabled={busy !== null || releasing}
                  onClick={releaseTagsOnly}
                >
                  Release Tags Only
                </button>
              ) : null}
            </div>
            {note.tagIds.length === 0 ? (
              <p className="btnhint">
                This Note Has No Tag, So There Is Nothing To Release Without The English. Try
                Again, Or Type The English Yourself Below.
              </p>
            ) : null}
          </>
        ) : null}

        {/*
         * The English box is ALWAYS editable and always present — it is §5's `Type It Yourself`
         * on the failure path, the review edit on the translated path, and the plain correction
         * on every other. One box rather than a mode, because the author's act is the same one
         * in all three cases: writing the English she is prepared to have released.
         */}
        <textarea
          className="natrev__english"
          value={english}
          rows={4}
          placeholder="Type the English yourself if you would rather."
          aria-label="The English version of this note"
          onChange={(event) => {
            setEnglish(event.target.value);
          }}
        />
        <div className="natrev__sub">
          <span>
            <span aria-hidden="true">✏︎ </span>Tap To Edit
          </span>
          <span className="natrev__notyet">{NOT_YET_RELEASED}</span>
        </div>
        {englishChanged ? (
          <button
            type="button"
            className="gbtn gbtn--sm"
            data-testid={`native-save-english-${note.id}`}
            disabled={busy !== null || (english.trim() === "" && note.tagIds.length === 0)}
            onClick={saveEnglish}
          >
            {busy === "english" ? "Saving…" : "Save The English"}
          </button>
        ) : null}
        {state === "untranslated" || state === "reviewed" ? (
          <button
            type="button"
            className="gbtn gbtn--sm"
            data-testid={`native-translate-${note.id}`}
            disabled={busy !== null}
            onClick={retranslate}
          >
            {state === "untranslated" ? "Translate Now" : "Translate Again"}
          </button>
        ) : null}
      </div>

      {/*
       * ── §2.6 · the privacy row ──────────────────────────────────────────────
       *
       * TWO LINES, IN §2.6's OWN STRUCTURE, and **no toggle**: the original's privacy is
       * unconditional, not a choice, so there is nothing here to switch.
       *
       * The wording constraint of SECURITY T4 §4.2 is load-bearing and must not be "improved".
       * `Stays Private` means the original is never shown to the other UMs or the TD — it is
       * processed only to produce the English version reviewed here. **This app must never say
       * the original never leaves your device**, because it does: it is sent to the AI service.
       * The sub-line below says so in as many words rather than leaving the stronger reading
       * available.
       */}
      <div className="lockrow natrev__privacy">
        <span aria-hidden="true">🔒</span>
        <span>
          <b>{privacyRowTitle(language)}</b>
          <br />
          {PRIVACY_ROW_SUBLINE}
          <br />
          <span className="natrev__privacysub">
            It Is Never Shown To The Other Managers Or The TD. It Is Processed Only To Produce
            The English Version You Review.
          </span>
        </span>
      </div>

      {/*
       * ── §2.7 · the tag chips ────────────────────────────────────────────────
       *
       * ONE LANGUAGE-NEUTRAL ID UNDERNEATH — that half of gap A1's answer is real and always
       * has been. The second label is not: there is no per-language tag label in this data, so
       * the English label is what is rendered, and the footer says so rather than letting the
       * absence read as a rendering fault. See the module header.
       */}
      {note.tagIds.length > 0 ? (
        <div className="natrev__tags">
          {note.tagIds.map((tagId) => (
            <span key={tagId} className="tag tag--on">
              {tagLabel(tagId)}
            </span>
          ))}
        </div>
      ) : null}

      {failure !== null ? (
        <p className="natrev__fail" role="status">
          {`${failure} Your Words Are Still Here — Nothing Was Lost.`}
        </p>
      ) : null}

      {/* ── §2.8 · the actions, as ruling §H leaves them ───────────────────── */}
      <div className="natrev__acts natrev__acts--main">
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-testid={`native-release-${note.id}`}
          disabled={!releasable || busy !== null || releasing}
          onClick={releaseEnglish}
        >
          {busy === "confirm" || releasing ? "Releasing…" : "Release English Version"}
        </button>
        <button type="button" className="gbtn" onClick={onClose}>
          Close
        </button>
      </div>
      {!releasable ? (
        <p className="btnhint">
          {state === "failed"
            ? "There Is No English To Release Yet. Try Again, Type It Yourself, Or Release The Tags Only."
            : state === "stale"
              ? "You Have Corrected The Original. The English Is Being Made Again — Release Waits For It."
              : "There Is No English Yet. Translate It, Or Type It Yourself."}
        </p>
      ) : null}

      {/*
       * §2.8's SECONDARY control, **as ruling §H (D-5) reconciles it**. The mockup drew
       * `Keep Private 🔒` as a choice taken at the moment of release; §H removed the
       * capture-time lock and made Lock an explicit press on a saved note, so the label and the
       * moment both move — the control is `🔒 Lock` on the note itself, and it lives in the
       * note's own row in `CapturePanel` beside every other note's. Release semantics are
       * unaffected: the English goes out, the original stays the author's for ever.
       *
       * It is deliberately NOT duplicated here. Two Lock buttons for one note, one of them
       * labelled from a superseded ruling, is how a build ends up with two answers to "what
       * does Lock mean".
       */}
      <p className="card__foot">
        {TAGS_ARE_NEUTRAL_FOOT} · Tag Labels Are Shown In English — No {language.name} Tag
        Labels Exist In This Build
      </p>
    </div>
  );
}
